import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Minus, Plus, ArrowLeft, ShieldAlert } from "lucide-react";
import { api } from "../../lib/api";
import { formatCurrency } from "../../lib/utils-format";
import { useAuth } from "../../contexts/AuthContext";
import { PhoneInput } from "../../components/PhoneInput";
import { SearchableSelect } from "../../components/SearchableSelect";

export default function BookingPage() {
    const { id } = useParams();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { isAuthenticated, profile } = useAuth();
    const [event, setEvent] = useState(null);
    const [quantities, setQuantities] = useState({});
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const [billing, setBilling] = useState({
        first_name: "",
        last_name: "",
        email: "",
        phone_code: "+1",
        phone: "",
        address: "",
        state: "",
        zip_code: "",
        city: "",
        country: "",
    });
    const [password, setPassword] = useState("");
    const [emailLookup, setEmailLookup] = useState({ checked: false, exists: false, loading: false });

    const checkEmail = async () => {
        if (!billing.email || !/^\S+@\S+\.\S+$/.test(billing.email)) return;
        setEmailLookup((s) => ({ ...s, loading: true }));
        try {
            const { data } = await api.post("/api/auth/check-email", { email: billing.email });
            setEmailLookup({ checked: true, exists: !!data?.exists, loading: false });
        } catch (e) {
            setEmailLookup({ checked: true, exists: false, loading: false });
        }
    };
    const resetEmailLookup = () => setEmailLookup({ checked: false, exists: false, loading: false });

    useEffect(() => {
        (async () => {
            try {
                const [a, b] = await Promise.all([
                    api.get(`/api/events/${id}`),
                    api.get("/api/countries"),
                ]);
                setEvent(a.data);
                setCountries(b.data || []);
                const init = {};
                (a.data.ticket_types || []).forEach((tt) => (init[tt.id] = 0));
                setQuantities(init);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    useEffect(() => {
        if (profile) {
            setBilling({
                first_name: profile.first_name || "",
                last_name: profile.last_name || "",
                email: profile.email || "",
                phone_code: profile.phone_code || "+1",
                phone: profile.phone || "",
                address: profile.address || "",
                state: profile.state || "",
                zip_code: profile.zip_code || "",
                city: profile.city || "",
                country: profile.country || "",
            });
        }
    }, [profile]);

    const { subtotal, items } = useMemo(() => {
        const list = [];
        let s = 0;
        (event?.ticket_types || []).forEach((tt) => {
            const q = quantities[tt.id] || 0;
            if (q > 0) {
                list.push({
                    ticket_type_id: tt.id,
                    name: tt.name,
                    price: tt.price,
                    quantity: q,
                });
                s += tt.price * q;
            }
        });
        return { subtotal: s, items: list };
    }, [quantities, event]);

    const [quote, setQuote] = useState(null);
    useEffect(() => {
        if (!event || items.length === 0) {
            setQuote(null);
            return;
        }
        const selections = items.map((i) => ({ ticket_type_id: i.ticket_type_id, quantity: i.quantity }));
        api.post("/api/bookings/quote", {
            event_id: event.id,
            selections,
            billing: {
                first_name: "x",
                last_name: "x",
                email: "x@x.com",
                phone_code: "+1",
                phone: "0",
                address: "x",
                state: "x",
                zip_code: "x",
                city: "x",
                country: "x",
            },
        })
            .then(({ data }) => setQuote(data))
            .catch(() => setQuote(null));
    }, [items, event]);

    if (loading) return <div className="p-10 text-muted-foreground">{t("common.loading")}</div>;
    if (!event) return <div className="p-10 text-muted-foreground">{t("common.not_found")}</div>;

    const currency = event.country?.currency_code || "USD";
    const now = new Date();
    const isPurchasable = (tt) => {
        if (typeof tt.available === "number" && tt.available <= 0) return false;
        if (tt.sale_start_datetime && new Date(tt.sale_start_datetime) > now) return false;
        if (tt.sale_end_datetime && new Date(tt.sale_end_datetime) < now) return false;
        return true;
    };
    const purchasableTickets = (event.ticket_types || []).filter(isPurchasable);
    const maxQtyFor = (tt) => Math.max(0, Math.min(tt.max_per_order || 10, tt.available || 0));

    const updateQty = (ttId, delta) => {
        const tt = (event.ticket_types || []).find((x) => x.id === ttId);
        if (!tt) return;
        const max = maxQtyFor(tt);
        const cur = quantities[ttId] || 0;
        const next = Math.max(0, Math.min(max, cur + delta));
        setQuantities({ ...quantities, [ttId]: next });
    };

    const submit = async () => {
        if (items.length === 0) {
            toast.error(t("booking.select_at_least_one"));
            return;
        }
        if (!isAuthenticated && (!emailLookup.checked || emailLookup.exists)) {
            toast.error(t("booking.account_exists_hint"));
            return;
        }
        // Validate min per order for each
        for (const it of items) {
            const tt = event.ticket_types.find((x) => x.id === it.ticket_type_id);
            if (it.quantity < (tt.min_per_order || 1)) {
                toast.error(t("booking.minimum_n", { n: tt.min_per_order, name: tt.name }));
                return;
            }
        }
        const required = ["first_name", "last_name", "email", "phone", "address", "state", "city", "country"];
        for (const k of required) {
            if (!billing[k]) {
                toast.error(t("auth.required"));
                return;
            }
        }
        if (!isAuthenticated && password.length < 6) {
            toast.error(t("auth.password_too_short"));
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/api/payments/checkout", {
                event_id: event.id,
                selections: items.map((i) => ({ ticket_type_id: i.ticket_type_id, quantity: i.quantity })),
                billing,
                origin_url: window.location.origin,
                create_account: !isAuthenticated,
                password: isAuthenticated ? null : password,
                preferred_language: i18n.language,
            });
            // Redirect
            if (data.checkout_url) {
                window.location.href = data.checkout_url;
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("common.error"));
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="booking-page" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
            <Link
                to={`/events/${event.id}`}
                data-testid="back-to-event"
                className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground mb-6"
            >
                <ArrowLeft className="h-4 w-4" />
                {t("booking.back_to_event")}
            </Link>
            <h1 className="font-heading font-black text-3xl md:text-4xl mb-2">{event.title}</h1>
            <p className="text-muted-foreground mb-8">{event.venue?.name}</p>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <div className="bento-card p-5 md:p-6">
                        <h2 className="font-heading font-bold text-xl mb-4">{t("booking.select_tickets")}</h2>
                        {purchasableTickets.length === 0 ? (
                            <div className="rounded-xl bg-muted p-6 text-center text-sm text-muted-foreground">
                                {t("event.no_tickets_available")}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {purchasableTickets.map((tt) => {
                                    const max = maxQtyFor(tt);
                                    const cur = quantities[tt.id] || 0;
                                    return (
                                        <div
                                            key={tt.id}
                                            data-testid={`select-ticket-${tt.id}`}
                                            className="flex items-center justify-between gap-4 p-4 rounded-xl border border-border"
                                        >
                                            <div className="flex-1">
                                                <div className="font-semibold">{tt.name}</div>
                                                <div className="text-sm text-muted-foreground">
                                                    {formatCurrency(tt.price, currency)}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <button
                                                    data-testid={`qty-minus-${tt.id}`}
                                                    onClick={() => updateQty(tt.id, -1)}
                                                    disabled={cur === 0}
                                                    className="w-10 h-10 rounded-full bg-muted flex items-center justify-center disabled:opacity-50 active:scale-95"
                                                >
                                                    <Minus className="h-4 w-4" />
                                                </button>
                                                <span
                                                    data-testid={`qty-value-${tt.id}`}
                                                    className="w-8 text-center font-bold text-lg"
                                                >
                                                    {cur}
                                                </span>
                                                <button
                                                    data-testid={`qty-plus-${tt.id}`}
                                                    onClick={() => updateQty(tt.id, 1)}
                                                    disabled={cur >= max}
                                                    className="w-10 h-10 rounded-full bg-foreground text-background flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
                                                >
                                                    <Plus className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Billing info */}
                    <div className="bento-card p-5 md:p-6">
                        <h2 className="font-heading font-bold text-xl mb-4">{t("booking.billing_info")}</h2>

                        {/* Email first — gates the rest of the form for guests */}
                        <div className="space-y-3">
                            <div>
                                <label className="text-sm font-semibold block mb-1.5">{t("auth.email")} *</label>
                                <div className="flex gap-2">
                                    <input
                                        data-testid="billing-email"
                                        type="email" required
                                        value={billing.email}
                                        onChange={(e) => { setBilling({ ...billing, email: e.target.value }); resetEmailLookup(); }}
                                        onBlur={() => { if (!isAuthenticated) checkEmail(); }}
                                        className="input-modern flex-1"
                                        disabled={isAuthenticated}
                                    />
                                    {!isAuthenticated && !emailLookup.checked && (
                                        <button
                                            type="button"
                                            data-testid="billing-email-check"
                                            onClick={checkEmail}
                                            disabled={emailLookup.loading || !billing.email}
                                            className="btn-outline disabled:opacity-50 whitespace-nowrap"
                                        >
                                            {emailLookup.loading ? t("common.loading") : t("booking.continue")}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {!isAuthenticated && emailLookup.checked && emailLookup.exists && (
                                <div data-testid="email-exists-banner" className="rounded-xl bg-muted p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                    <div className="text-sm">
                                        <span className="font-bold">{t("booking.account_exists_title")}</span>
                                        <p className="text-muted-foreground mt-0.5">{t("booking.account_exists_hint")}</p>
                                    </div>
                                    <Link
                                        to="/login"
                                        data-testid="email-exists-login-link"
                                        className="btn-primary whitespace-nowrap"
                                    >
                                        {t("auth.login")}
                                    </Link>
                                </div>
                            )}
                        </div>

                        {/* Rest of the form — shown when logged-in OR guest with confirmed-new email */}
                        {(isAuthenticated || (emailLookup.checked && !emailLookup.exists)) && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                                <Input label={t("auth.first_name")} val={billing.first_name} set={(v) => setBilling({ ...billing, first_name: v })} required testId="billing-first-name" />
                                <Input label={t("auth.last_name")} val={billing.last_name} set={(v) => setBilling({ ...billing, last_name: v })} required testId="billing-last-name" />
                                <div>
                                    <PhoneInput
                                        label={t("auth.phone")}
                                        required
                                        phoneCode={billing.phone_code}
                                        phone={billing.phone}
                                        onChange={({ phone_code, phone }) => setBilling({ ...billing, phone_code, phone })}
                                        testPrefix="billing-phone"
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <Input label={t("auth.address")} val={billing.address} set={(v) => setBilling({ ...billing, address: v })} required testId="billing-address" />
                                </div>
                                <div>
                                    <label className="text-sm font-semibold block mb-1.5">{t("auth.country")} *</label>
                                    <SearchableSelect
                                        testId="billing-country"
                                        options={countries.map((c) => ({ value: c.name, label: c.name }))}
                                        value={billing.country}
                                        onValueChange={(v) => setBilling({ ...billing, country: v })}
                                        placeholder={t("common.choose")}
                                    />
                                </div>
                                <Input label={t("auth.state")} val={billing.state} set={(v) => setBilling({ ...billing, state: v })} required testId="billing-state" />
                                <Input label={t("auth.city")} val={billing.city} set={(v) => setBilling({ ...billing, city: v })} required testId="billing-city" />
                                <Input label={t("auth.zip_code")} val={billing.zip_code} set={(v) => setBilling({ ...billing, zip_code: v })} testId="billing-zip" />
                            </div>
                        )}

                        {!isAuthenticated && emailLookup.checked && !emailLookup.exists && (
                            <div className="mt-4 p-4 rounded-xl bg-muted space-y-2">
                                <p className="text-sm font-semibold">{t("booking.create_account")}</p>
                                <p className="text-xs text-muted-foreground">{t("booking.create_account_hint")}</p>
                                <input
                                    data-testid="booking-password"
                                    type="password"
                                    placeholder={t("booking.password")}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="input-modern"
                                    minLength={6}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Summary */}
                <div className="lg:col-span-1">
                    <div className="bento-card p-5 md:p-6 sticky top-20 space-y-4">
                        <h2 className="font-heading font-bold text-xl">{t("booking.payment_info")}</h2>
                        <div className="space-y-2 text-sm">
                            {items.map((i) => (
                                <div key={i.ticket_type_id} className="flex justify-between">
                                    <span className="text-muted-foreground">
                                        {i.name} × {i.quantity}
                                    </span>
                                    <span className="font-semibold">
                                        {formatCurrency(i.price * i.quantity, currency)}
                                    </span>
                                </div>
                            ))}
                            <hr className="my-2 border-border" />
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">{t("booking.subtotal")}</span>
                                <span className="font-semibold">{formatCurrency(subtotal, currency)}</span>
                            </div>
                            {quote && (
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">{t("booking.fee")}</span>
                                    <span className="font-semibold">{formatCurrency(quote.fee_amount, currency)}</span>
                                </div>
                            )}
                            <hr className="my-2 border-border" />
                            <div className="flex justify-between text-lg">
                                <span className="font-bold">{t("booking.total")}</span>
                                <span className="font-heading font-black">
                                    {formatCurrency(quote?.total || subtotal, currency)}
                                </span>
                            </div>
                        </div>
                        <div className="flex items-start gap-2 p-3 rounded-xl bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] text-xs font-semibold">
                            <ShieldAlert className="h-4 w-4 flex-shrink-0 mt-0.5" />
                            <span>{t("booking.disclaimer")}</span>
                        </div>
                        <button
                            data-testid="pay-btn"
                            onClick={submit}
                            disabled={submitting || items.length === 0}
                            className="btn-primary w-full disabled:opacity-50"
                        >
                            {submitting
                                ? t("common.loading")
                                : t("booking.pay", { total: formatCurrency(quote?.total || subtotal, currency) })}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

const Input = ({ label, val, set, type = "text", required, testId }) => (
    <div>
        <label className="text-sm font-semibold block mb-1.5">{label}{required ? " *" : ""}</label>
        <input
            data-testid={testId}
            type={type}
            value={val || ""}
            required={required}
            onChange={(e) => set(e.target.value)}
            className="input-modern"
        />
    </div>
);
